import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useEscape } from '@core/shortcuts'

// ---------------------------------------------------------------------------
// Menu de um comando do friso, desenhado FORA da barra.
//
// A fila de painéis do friso tem `overflow-x-auto`, porque com muitos
// comandos tem de deslizar de lado. Um menu em `position: absolute`
// dentro dela é recortado por esse overflow: em vez de passar por cima
// do modelo, ficava cortado e obrigava a deslizar a barra para o ler,
// que é trabalho a mais para escolher uma opção.
//
// Este componente escreve o menu num portal para o `body`, em
// `position: fixed`, ancorado ao botão que o abriu. Passa por cima da
// área do modelo, que é onde há espaço.
//
// A POSIÇÃO NÃO É ESTADO. É escrita directamente no nó, no
// `useLayoutEffect`: medir depois do layout e antes de pintar é o que
// evita ver o menu aparecer no sítio errado e saltar a seguir. Guardá-la
// em estado obrigava a um render por cada evento de scroll, e o scroll
// da barra dispara muitos.
//
// Trata também do resto do que um menu ancorado tem de tratar: vira-se
// para cima quando não há espaço em baixo, encosta-se à janela em vez de
// sair dela, fecha com Escape ou com um clique fora, e acompanha o botão
// quando a barra desliza ou a janela muda de tamanho.
// ---------------------------------------------------------------------------

/** Folga entre o botão e o menu, em pixéis. */
const FOLGA = 4
/** Margem mínima às bordas da janela. */
const MARGEM = 8
/** Abaixo desta altura útil, o menu prefere abrir para cima. */
const ALTURA_MINIMA = 160

export interface RibbonMenuProps {
  open: boolean
  /** O elemento a que o menu se ancora (normalmente o invólucro do botão). */
  anchorRef: React.RefObject<HTMLElement | null>
  /** Fechar: clique fora ou Escape. */
  onClose: () => void
  width: number
  /** Alinhamento ao âncora. 'center' centra; 'left' alinha à esquerda. */
  align?: 'left' | 'center'
  /** Altura máxima antes de o próprio menu passar a deslizar. */
  maxHeight?: number
  role?: string
  ariaLabel?: string
  children: ReactNode
}

export default function RibbonMenu({
  open,
  anchorRef,
  onClose,
  width,
  align = 'left',
  maxHeight,
  role = 'dialog',
  ariaLabel,
  children,
}: RibbonMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    if (!open) return
    const no = menuRef.current
    const ancora = anchorRef.current
    if (!no || !ancora) return

    const medir = () => {
      const r = ancora.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight

      const abaixo = vh - r.bottom - FOLGA - MARGEM
      const acima = r.top - FOLGA - MARGEM
      // Vira para cima só quando em baixo não cabe um menu utilizável e
      // em cima cabe melhor.
      const paraCima = abaixo < ALTURA_MINIMA && acima > abaixo
      const disponivel = Math.max(120, paraCima ? acima : abaixo)
      const altura = Math.min(maxHeight ?? disponivel, disponivel)

      let left = align === 'center' ? r.left + r.width / 2 - width / 2 : r.left
      left = Math.min(Math.max(MARGEM, left), vw - width - MARGEM)

      no.style.left = `${Math.round(left)}px`
      no.style.top = `${Math.round(paraCima ? r.top - FOLGA - altura : r.bottom + FOLGA)}px`
      no.style.maxHeight = `${Math.round(altura)}px`
      no.style.visibility = 'visible'
    }

    medir()
    // `capture` para apanhar também o scroll da fila de painéis, que não
    // borbulha até à janela.
    window.addEventListener('scroll', medir, true)
    window.addEventListener('resize', medir)
    return () => {
      window.removeEventListener('scroll', medir, true)
      window.removeEventListener('resize', medir)
    }
  }, [open, anchorRef, width, align, maxHeight])

  // Escape fecha o menu antes de chegar à cascata do viewer.
  useEscape(open, onClose)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    const onDown = (e: PointerEvent) => {
      const alvo = e.target as Node
      if (menuRef.current?.contains(alvo)) return
      if (anchorRef.current?.contains(alvo)) return
      onClose()
    }
    window.addEventListener('keydown', onKey)
    // `pointerdown` e não `click`: fecha ao começar o gesto, como os
    // menus do sistema.
    window.addEventListener('pointerdown', onDown)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('pointerdown', onDown)
    }
  }, [open, onClose, anchorRef])

  if (!open) return null

  return createPortal(
    <div
      ref={menuRef}
      role={role}
      aria-label={ariaLabel}
      // `visibility: hidden` até a primeira medição: o nó tem de existir
      // para ser medido, mas não deve ser visto no canto superior
      // esquerdo antes de saber onde fica.
      style={{ position: 'fixed', left: 0, top: 0, width, visibility: 'hidden' }}
      className="z-[900] overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-1 text-[var(--color-text)] shadow-2xl"
    >
      {children}
    </div>,
    document.body,
  )
}
